/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import logo from "@/assets/logo-horizontal-orange.svg"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { plataformaAutenticacaoToken, authenticate } from "@/modules/Auth/service/AuthServices"
import { Input } from "@/components/ui/input"
import { useToast } from "@/contexts/ToastContext"
import { EyeIcon, EyeOffIcon } from "lucide-react"
import { Icon } from "@/components/Atoms/Icon"
import { LOGOUT_REASON_KEY } from "@/api/axios.config"

const isRNWebView = typeof window !== "undefined" && !!(window as any).ReactNativeWebView

const LOGOUT_REASON_MESSAGES: Record<string, string> = {
  EMPRESA_INATIVA: "Sua empresa está inativa. Contate o suporte.",
  CONTRATO_INATIVO: "Sua empresa está sem contrato ativo. Contate o suporte.",
  EMPRESA_NAO_IDENTIFICADA: "Não foi possível identificar sua empresa. Faça login novamente.",
  EMPRESA_NAO_ENCONTRADA: "Empresa não encontrada. Contate o suporte.",
}

export default function BackofficeLogin() {
  const [step, setStep] = useState<
    "credentials" | "otp" | "new_pass" | "password" | "recovered" | "forgot_otp" | "reset_password"
  >("credentials")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState<string>("")
  const [confirmPassword, setConfirmPassword] = useState<string>("")
  const [otp, setOtp] = useState("")
  const [resetToken, setResetToken] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const toast = useToast()

  const getRedirectPath = () => {
    const returnTo = searchParams.get("returnTo")
    return returnTo ? decodeURIComponent(returnTo) : "/dashboard"
  }

  const {
    plataformaResetSenha,
    plataformaLogin,
    loginWithFaceId,
    handleRequestReset,
    handleValidateOtp,
    handleConfirmReset,
  } = useAuth()

  const faceIdHandledRef = useRef(false)

  const navigateAfterLogin = () => {
    if (isRNWebView && !localStorage.getItem("faceIdRefreshToken")) {
      sessionStorage.setItem("showFaceIdPrompt", "1")
    }
    navigate(getRedirectPath())
  }

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    plataformaAutenticacaoToken(email)
      .then(({ data }) => {
        if (data.data.proximoPasso === "otp") {
          setStep("otp")
        } else if (data.data.proximoPasso === "criar_senha") {
          toast.success(
            `Parabéns pelo seu primeiro acesso, enviamos um email para ${email} para criar uma senha.`
          )
          setStep("recovered")
        } else {
          setStep("password")
        }
      })
      .catch((error) => {
        toast.danger(error.response.data.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleOtpComplete = async (value: string) => {
    setOtp(value)
    if (value.length === 4) {
      setLoading(true)
      plataformaLogin(email, value, "otp")
        .then(() => navigateAfterLogin())
        .finally(() => setLoading(false))
    }
  }

  const handleRecoverPass = async () => {
    setLoading(true)
    try {
      await handleRequestReset(email)
      setStep("forgot_otp")
    } finally {
      setLoading(false)
    }
  }

  const handleRecoveryOtp = async (value: string) => {
    setOtp(value)
    if (value.length === 4) {
      setLoading(true)
      try {
        const token = await handleValidateOtp(email, value)
        setResetToken(token)
        setStep("reset_password")
      } finally {
        setLoading(false)
      }
    }
  }

  const handleFinalReset = async () => {
    if (password !== confirmPassword) {
      toast.danger("As senhas não coincidem")
      return
    }
    setLoading(true)
    try {
      await handleConfirmReset(resetToken, password)
    } catch {
      setLoading(false)
    }
  }

  const handleSetNewPass = async () => {
    setLoading(true)
    plataformaResetSenha(email, password, localStorage.getItem("token"))
      .then(() => navigate(getRedirectPath()))
      .finally(() => setLoading(false))
  }

  const handleLogin = async () => {
    setLoading(true)
    plataformaLogin(email, password, "senha")
      .then(() => navigateAfterLogin())
      .finally(() => setLoading(false))
  }

  // Após montar: se Face ID está configurado e usuário não está logado, sinaliza o nativo
  useEffect(() => {
    if (!isRNWebView) return
    const configured = !!localStorage.getItem("faceIdRefreshToken")
    const isLoggedIn = !!localStorage.getItem("token") && !!localStorage.getItem("user")
    if (configured && !isLoggedIn) {
      const timer = setTimeout(() => {
        ;(window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ type: "FACE_ID_STATUS", shouldTrigger: true })
        )
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [])

  // Escuta FACE_AUTH_SUCCESS / FACE_AUTH_ERROR do nativo
  useEffect(() => {
    if (!isRNWebView) return
    const handleFaceAuthMessage = async (event: MessageEvent) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data

        if (data?.type === "FACE_AUTH_ERROR") {
          faceIdHandledRef.current = false
          return
        }

        if (data?.type !== "FACE_AUTH_SUCCESS") return
        if (faceIdHandledRef.current) return

        const rt = localStorage.getItem("faceIdRefreshToken")
        const userStr = localStorage.getItem("faceIdUser")
        if (!rt || !userStr) return

        faceIdHandledRef.current = true
        try {
          await loginWithFaceId(rt, JSON.parse(userStr))
          navigate(getRedirectPath())
        } catch {
          faceIdHandledRef.current = false
        }
      } catch {}
    }

    window.addEventListener("message", handleFaceAuthMessage)
    return () => window.removeEventListener("message", handleFaceAuthMessage)
  }, [])

  useEffect(() => {
    const logoutReason = sessionStorage.getItem(LOGOUT_REASON_KEY)
    if (logoutReason) {
      sessionStorage.removeItem(LOGOUT_REASON_KEY)
      toast.danger(LOGOUT_REASON_MESSAGES[logoutReason] ?? "Sua sessão foi encerrada. Faça login novamente.")
    }

    if (
      localStorage.getItem("token") &&
      localStorage.getItem("user") &&
      localStorage.getItem("refreshToken")
    ) {
      navigate(getRedirectPath())
      return
    }
    authenticate()
      .then(({ authData }) => {
        if (!localStorage.getItem("refreshToken")) {
          localStorage.setItem("token", authData.access_token)
        }
      })
      .catch(console.error)
  }, [])

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[url('/src/assets/background-grain.png')] bg-cover bg-center bg-no-repeat p-6">
      <div className="z-20 w-full max-w-md space-y-8">
        <div className="space-y-6 overflow-hidden rounded-lg bg-black/70 p-8">
          {step === "password" && (
            <button
              onClick={() => {
                setEmail("")
                setStep("credentials")
              }}
              className="flex gap-2 text-zinc-500"
            >
              <Icon name="angle-left" />
              <span>Voltar</span>
            </button>
          )}
          <div className="flex justify-center">
            <img
              src={logo}
              alt="Giants"
              width="50%"
            />
          </div>

          {step !== "recovered" && step !== "password" && (
            <div className="space-y-2 text-center">
              <p className="text-sm text-muted-foreground">Digite seu email para efetuar o login</p>
            </div>
          )}

          {step === "credentials" && (
            <form
              onSubmit={handleCredentialsSubmit}
              className="space-y-4"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="giants@exemplo.com.br"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full rounded-lg bg-primary py-3 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? "Verificando..." : "Continuar"}
              </button>

            </form>
          )}

          {step === "otp" && (
            <div className="space-y-6">
              <div className="space-y-2 text-center">
                <h2 className="text-lg font-semibold text-foreground">Código de Verificação</h2>
                <p className="text-sm text-muted-foreground">
                  Digite o código enviado para {email}
                </p>
              </div>
              <div className="flex justify-center">
                <Input
                  type="text"
                  onChange={(e) => handleOtpComplete(e.target.value)}
                  className="h-12 border-border bg-surface text-center text-2xl tracking-widest text-foreground"
                  placeholder="0000"
                  maxLength={4}
                  inputMode="numeric"
                  required
                />
              </div>
              <button
                onClick={() => setStep("credentials")}
                className="w-full text-sm text-muted-foreground"
              >
                Voltar
              </button>
            </div>
          )}

          {step === "forgot_otp" && (
            <div className="space-y-6">
              <div className="space-y-2 text-center">
                <h2 className="text-lg font-semibold text-foreground">Recuperação de Senha</h2>
                <p className="text-sm text-muted-foreground">
                  Digite o código de 4 dígitos enviado por email
                </p>
              </div>
              <div className="flex justify-center">
                <Input
                  type="text"
                  onChange={(e) => handleRecoveryOtp(e.target.value)}
                  className="h-12 border-border bg-surface text-center text-2xl tracking-widest text-foreground"
                  placeholder="0000"
                  maxLength={4}
                  inputMode="numeric"
                  required
                />
              </div>
              <button
                onClick={() => setStep("password")}
                className="w-full text-sm text-muted-foreground"
              >
                Voltar
              </button>
            </div>
          )}

          {step === "reset_password" && (
            <div className="space-y-6">
              <div className="space-y-2 text-center">
                <h2 className="text-lg font-semibold text-foreground">Nova Senha</h2>
              </div>
              <div className="relative">
                <Input
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite sua nova senha"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeIcon size={20} /> : <EyeOffIcon size={20} />}
                </button>
              </div>
              <div className="relative">
                <Input
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirme sua nova senha"
                  required
                />
              </div>
              <button
                onClick={handleFinalReset}
                disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                className="w-full rounded-lg bg-primary py-3 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? "Salvando..." : "Alterar Senha"}
              </button>
            </div>
          )}

          {step === "password" && (
            <div className="space-y-6">
              <div className="space-y-2 text-center">
                <h2 className="text-lg font-semibold text-foreground">Digite sua senha</h2>
              </div>
              <div className="relative">
                <Input
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  placeholder="**********"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeIcon size={20} /> : <EyeOffIcon size={20} />}
                </button>
              </div>
              <button
                onClick={handleLogin}
                disabled={loading || !password}
                className="w-full rounded-lg bg-primary py-3 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? "Carregando..." : "Entrar"}
              </button>
              <button
                onClick={handleRecoverPass}
                className="flex w-full items-center justify-center p-1 underline"
              >
                Recuperar senha
              </button>
            </div>
          )}

          {step === "recovered" && (
            <div className="space-y-6 overflow-hidden">
              <div className="space-y-2 text-center">
                <h2 className="w-full truncate text-lg text-orange-500">
                  <div className="text-foreground">Acesse o link enviado para</div>
                  <strong className="w-full text-sm text-orange-500">{email}</strong>
                  <div className="text-foreground">para cadastrar sua nova senha.</div>
                </h2>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute size-[500px] animate-ripple rounded-full border-[10px] border-white blur-[30px] transition-opacity"></div>
      <div className="absolute size-[1000px] animate-ripple rounded-full border-[10px] border-white blur-[40px] transition-opacity [animation-delay:.5s]"></div>
      <div className="absolute size-[1800px] animate-ripple rounded-full border-[10px] border-white blur-[50px] transition-opacity [animation-delay:.7s]"></div>

    </div>
  )
}
